// SuperAdmin Page - Refactored version using modular components
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { RefreshCw, LayoutDashboard, CreditCard, Building2, Users, BarChart3, Archive, Bell, Settings, Receipt } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

// Import admin components
import { 
  AdminProvider, 
  useAdmin,
  AdminDashboardTab,
  AdminPlansTab,
  AdminTenantsTab,
  AdminUsersTab,
  AdminAnalyticsTab,
  AdminBackupTab,
  AdminNotificationsTab,
  AdminSettingsTab,
  AdminManualPaymentsTab
} from '../admin';

function SuperAdminContent() {
  const { user } = useAuthStore();
  const { loading, fetchAll } = useAdmin();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = async () => {
    await fetchAll();
  };

  return (
    <div className="p-4 space-y-4" data-testid="superadmin-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-yellow-500">SuperAdmin Dashboard</h1>
          <p className="text-sm text-gray-400">Welcome, {user?.name || user?.email}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-9 w-full">
          <TabsTrigger value="dashboard" className="flex items-center gap-1">
            <LayoutDashboard className="w-4 h-4" />
            <span className="hidden md:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-1">
            <CreditCard className="w-4 h-4" />
            <span className="hidden md:inline">Plans</span>
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            <span className="hidden md:inline">Tenants</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span className="hidden md:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-1">
            <Receipt className="w-4 h-4" />
            <span className="hidden md:inline">Payments</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden md:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-1">
            <Archive className="w-4 h-4" />
            <span className="hidden md:inline">Backup</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-1">
            <Bell className="w-4 h-4" />
            <span className="hidden md:inline">Notify</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1">
            <Settings className="w-4 h-4" />
            <span className="hidden md:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <AdminDashboardTab auditLogs={auditLogs} />
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <AdminPlansTab />
        </TabsContent>

        <TabsContent value="tenants" className="mt-4">
          <AdminTenantsTab />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <AdminUsersTab />
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <AdminManualPaymentsTab />
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <AdminAnalyticsTab />
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          <AdminBackupTab />
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <AdminNotificationsTab />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <AdminSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function SuperAdminPage() {
  return (
    <AdminProvider>
      <SuperAdminContent />
    </AdminProvider>
  );
}
